import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, map, of, tap } from 'rxjs';

import { ApiResponse, FrontendPageSeo } from '../api/api.models';
import { SAVING_ARENA_API_CONFIG } from '../api/api.config';

/**
 * Admin-set meta title / description overrides, keyed by canonical path.
 *
 * These exist for the pages that have no admin screen of their own — /storelist, /events, /compare,
 * /coupons, the shop, the static informational pages — whose meta was previously written into each
 * component and could only be changed by editing code.
 *
 * Why module state and an app initializer rather than a service the pages inject: `SeoService.apply()`
 * is synchronous and is called by every page the moment its data resolves, so there is no point at
 * which it could await a lookup. Filling a registry before the router's first navigation is the same
 * shape `discount-type-registry` and `blog-slug-registry` already use here, for the same reason.
 *
 * The alternative — teaching each of the ~18 affected pages to fetch its own override — would have
 * been eighteen chances to forget one, and every new page would start out uneditable again. Sitting
 * in `apply()` means a page is covered by virtue of setting its SEO at all.
 */

/** Overrides by canonical path. Paths are normalised the same way `SeoService` normalises them. */
let overridesByPath = new Map<string, FrontendPageSeo>();

// Angular SSR bootstraps the app once per request, so without a TTL this initializer would put a
// lookup on the critical path of every page render. Module state outlives the request on the server,
// so this collapses to roughly one refresh per minute per server process while still picking up an
// admin's edit without a restart. Matches `discount-type-registry`.
const RELOAD_AFTER_MS = 60_000;
let loadedAtMs = 0;

/**
 * Same normalisation `SeoService` applies to a canonical path: no query or hash, lower case, no
 * trailing slash. Both sides must agree or a saved override would never be found.
 */
function normalizePath(path: string): string {
  const withoutQuery = String(path || '/')
    .split('#')[0]
    .split('?')[0];
  const trimmed = withoutQuery.toLowerCase().replace(/\/+$/, '');

  return trimmed === '' ? '/' : trimmed;
}

/** The admin's override for a path, or null when there is none. */
export function getPageSeoOverride(path: string): FrontendPageSeo | null {
  return overridesByPath.get(normalizePath(path)) ?? null;
}

/** Replaces the registry. Exported for tests and the initializer below. */
export function setPageSeoOverrides(next: readonly FrontendPageSeo[]): void {
  const map = new Map<string, FrontendPageSeo>();

  for (const entry of next) {
    const path = normalizePath(entry?.path ?? '');
    // A row carrying neither field is not an override — it is a page an admin opened and left
    // alone. Storing it would mean checking for empty strings at every read site instead of here.
    if (!path || (!entry.metaTitle?.trim() && !entry.metaDescription?.trim())) continue;

    map.set(path, {
      path,
      metaTitle: entry.metaTitle?.trim() || '',
      metaDescription: entry.metaDescription?.trim() || '',
    });
  }

  overridesByPath = map;
}

/**
 * App initializer: loads the overrides before the router's first navigation, so the first page
 * rendered already has them.
 *
 * Fails soft. An override is an improvement on the built-in wording, never a requirement for it, so
 * a failed lookup leaves every page with the meta it shipped with rather than breaking the boot.
 */
export function loadPageSeoOverrides() {
  const http = inject(HttpClient);
  const config = inject(SAVING_ARENA_API_CONFIG);

  const now = Date.now();
  if (loadedAtMs && now - loadedAtMs < RELOAD_AFTER_MS) return of(undefined);
  loadedAtMs = now;

  // apiBaseUrl already ends in /api/frontend — do not repeat it here.
  return http.get<ApiResponse<FrontendPageSeo[]>>(`${config.apiBaseUrl}/settings/page-seo`).pipe(
    tap((response) => {
      if (response?.success && Array.isArray(response.data)) {
        setPageSeoOverrides(response.data);
      }
    }),
    catchError(() => {
      // Let the next render retry rather than sitting on nothing for a full TTL.
      loadedAtMs = 0;
      return of(null);
    }),
    map(() => undefined),
  );
}

/** Test seam: forget the TTL and the overrides so the next call refetches. */
export function resetPageSeoOverrides(): void {
  loadedAtMs = 0;
  overridesByPath = new Map();
}
